import Vec2 from 'vec2';

import {
  CornerInfo,
  CornerMap,
  DrawingEdge,
  DrawingShape,
} from '~/core/models';
import { getSideItemsFromShape } from '~/core/utils';

interface UpdateEdgesWithNewOutlineDataReturn {
  corners: CornerMap;
  edges: DrawingEdge[];
}

export const updateEdgesWithNewOutlineData = (
  oldShape: DrawingShape,
  outlineData: Partial<CornerInfo>[]
): UpdateEdgesWithNewOutlineDataReturn => {
  const corners: CornerMap = {};
  const oldCornersKeys = Object.keys(oldShape.cornerMap);
  const oldCornerValues = Object.values(oldShape.cornerMap);
  const oldSideItems = getSideItemsFromShape(oldShape);

  // update edges as needed based on new outline data, referencing old edges when required
  const edges = oldCornersKeys.map((key, index) => {
    const outlinePoint = outlineData[index];
    const nextOutlinePoint =
      outlineData[index === oldCornersKeys.length - 1 ? 0 : index + 1];

    if (!outlinePoint?.coords || !nextOutlinePoint?.coords) {
      throw new Error(`Missing coords for outline points at index ${index}`);
    }

    corners[key] = oldShape.cornerMap[key];
    corners[key].coords = outlinePoint.coords;

    const startCornerId = corners[key].id;
    const endCornerIndex = index === oldCornersKeys.length - 1 ? 0 : index + 1;
    const endCornerId = oldCornerValues[endCornerIndex].id;

    const dirVec = new Vec2(
      nextOutlinePoint.coords.x - outlinePoint.coords.x,
      nextOutlinePoint.coords.y - outlinePoint.coords.y
    );

    const normalVecX =
      dirVec.x * Math.cos(Math.PI / 2) - dirVec.y * Math.sin(Math.PI / 2);
    const normalVecY =
      dirVec.x * Math.sin(Math.PI / 2) + dirVec.y * Math.cos(Math.PI / 2);
    const normalVecLength = Math.sqrt(
      normalVecX * normalVecX + normalVecY * normalVecY
    );

    const normalVec = new Vec2(
      normalVecLength > 0 ? normalVecX / normalVecLength : 0,
      normalVecLength > 0 ? normalVecY / normalVecLength : 0
    );
    const isVisibleInCanvas = oldShape.drawingEdges[index].isVisibleInCanvas;

    if (!oldSideItems.length) {
      oldShape.drawingEdges[index].sideItem.arrowsInfo = [];
    }

    const matchingOldEdge = oldShape.drawingEdges.find(
      (edge) =>
        edge.startCornerId === startCornerId && edge.endCornerId === endCornerId
    );

    const edge = {
      id: oldShape.drawingEdges[index].id,
      startCornerId,
      endCornerId,
      startDisplayVec: new Vec2(0, 0),
      endDisplayVec: new Vec2(0, 0),
      normalVec,
      dirVec,
      isVisibleInCanvas,
      drawingShapeId: oldShape.id,
      sideItem:
        matchingOldEdge?.sideItem || oldShape.drawingEdges[index].sideItem,
      bumpOut: oldShape.drawingEdges[index].bumpOut,
      customSideType: oldShape.drawingEdges[index].customSideType,
      realRadius: oldShape.drawingEdges[index].realRadius,
      radiusSagitta: oldShape.drawingEdges[index].radiusSagitta,
    } as DrawingEdge;

    Object.defineProperty(edge, 'startDisplayVec', {
      value: edge.startDisplayVec,
      writable: true,
      enumerable: true,
      configurable: true,
    });

    Object.defineProperty(edge, 'endDisplayVec', {
      value: edge.endDisplayVec,
      writable: true,
      enumerable: true,
      configurable: true,
    });

    Object.defineProperty(edge, 'dirVec', {
      value: edge.dirVec,
      writable: true,
      enumerable: true,
      configurable: true,
    });

    Object.defineProperty(edge, 'normalVec', {
      value: edge.normalVec,
      writable: true,
      enumerable: true,
      configurable: true,
    });

    return edge;
  });

  return {
    corners,
    edges,
  };
};
